import { useEffect, useMemo } from 'react';
import type { TFunction } from 'i18next';
import { convertToUUID } from 'uuid-encoding';
import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';
import { useVisitorCustomFieldConfigs } from './useVisitorCustomFieldConfigs';

type ColumnVisibilityState = Record<string, boolean>;

export function useVisitorCustomFieldColumns({
  channelId,
  visibleColumns,
  t,
}: {
  channelId: string;
  visibleColumns: ColumnVisibilityState | undefined;
  t: TFunction;
}) {
  const customFieldsEnabled = useFlag(
    FeatureFlag.VisitorManagementCustomFields,
    false
  );
  const {
    data: visitorCustomFieldConfigs,
    fetch: fetchVisitorCustomFieldConfigs,
  } = useVisitorCustomFieldConfigs();

  useEffect(() => {
    if (!customFieldsEnabled || !channelId) return;

    fetchVisitorCustomFieldConfigs(convertToUUID(channelId));
  }, [channelId, customFieldsEnabled, fetchVisitorCustomFieldConfigs]);

  const customFieldColumns = useMemo(
    () =>
      (customFieldsEnabled
        ? visitorCustomFieldConfigs.filter(customField => customField.isEnabled)
        : []
      ).map(customFieldConfig => ({
        header: customFieldConfig.fieldName,
        key: `customField.${customFieldConfig.id}`,
        customFieldSection: t('web.table.editColumns.section.customFields'),
        renderCell: () => null,
        renderForCSV: () => '',
      })),
    [customFieldsEnabled, visitorCustomFieldConfigs, t]
  );

  const effectiveColumnVisibility = useMemo(() => {
    const base = visibleColumns ?? {};
    const hidden: ColumnVisibilityState = {};

    for (const column of customFieldColumns) {
      if (!(column.key in base)) {
        hidden[column.key] = false;
      }
    }

    return Object.keys(hidden).length > 0 ? { ...base, ...hidden } : visibleColumns;
  }, [visibleColumns, customFieldColumns]);

  return { customFieldColumns, effectiveColumnVisibility, customFieldsEnabled };
}
